import { useCallback, useMemo } from 'react';
import { TOP_HEADERS_HEIGHT } from '../CONSTANTS';
import { IconButton, IStackProps, Stack, Text, useTheme } from '@fluentui/react';
import { ExpandedRows, isPseudoRow, useApprovalStore } from '../../../../../_grid/grid3';
import shallow from 'zustand/shallow';
import { useGridLayoutContext } from '../context/LayoutProvider';
import { useDrag } from '@use-gesture/react';
import { useConfigValuesState } from '../../../../../hooks/useConfigValue';

export const Headers = ({ start }: { start: number }) => {
    const theme = useTheme();
    const { gridRowStyles, setRowHeaderWidth } = useGridLayoutContext();
    const styles = useMemo(() => {
        return {
            ...gridRowStyles,
            position: 'sticky',
            transform: `translateY(${start}px)`,
            height: TOP_HEADERS_HEIGHT,
            top: 0,
            left: 0,
            boxSizing: 'border-box',
            backgroundColor: theme.semanticColors.bodyBackground,
            zIndex: 9999,
            alignItems: 'center',
            borderBottom: `1px solid ${theme.semanticColors.disabledBackground}`,
        } as React.CSSProperties;
    }, [gridRowStyles, start, theme.semanticColors.bodyBackground, theme.semanticColors.disabledBackground]);

    const allRows = useApprovalStore(store => store.rows);
    const [expandedState, setExpandedState] = useApprovalStore(store => [store.expandedRows, store.setExpandedRows] as const, shallow);

    const checkState = useMemo(() => {
        const subrowIds = allRows.filter(isPseudoRow).map(row => row.id);
        if (subrowIds.length && subrowIds.every(id => expandedState[id])) {
            return false;
        }
        return true;
    }, [allRows, expandedState]);

    const onChange = useCallback(() => {
        if (checkState) {
            setExpandedState(
                allRows.reduce((newState, row) => {
                    newState[row.id] = true;
                    return newState;
                }, {} as ExpandedRows),
            );
        } else {
            setExpandedState(
                allRows.reduce((newState, row) => {
                    newState[row.id] = false;
                    return newState;
                }, {} as ExpandedRows),
            );
        }
    }, [setExpandedState, allRows, checkState]);

    const {
        initialData: { configuration },
    } = useConfigValuesState();

    const bind = useDrag(
        useCallback(
            ({ delta: [dx] }) => {
                setRowHeaderWidth(width => {
                    const nextWidth = width + dx;
                    if (nextWidth < 150) {
                        return 150;
                    }
                    return nextWidth;
                });
            },
            [setRowHeaderWidth],
        ),
        { axis: 'x' },
    );

    return (
        <div style={styles}>
            <Stack horizontal verticalAlign="center" styles={{ root: { position: 'relative' } }} tokens={{ childrenGap: 10 }}>
                <IconButton
                    iconProps={{
                        iconName: 'ChevronRight',
                        style: {
                            color: theme.semanticColors.bodyText,
                            transform: checkState ? 'rotate(0deg)' : 'rotate(90deg)',
                            transition: 'transform 300ms',
                        },
                    }}
                    onClick={onChange}
                />
                <Text>
                    <strong>Name/Task Name</strong>
                </Text>
                <Stack {...bind()} styles={dragHandleStyles}></Stack>
            </Stack>
            <Text>
                <strong>Hours (hrs)</strong>
            </Text>
            <Text>
                <strong>Billable (hrs)</strong>
            </Text>
            <Text>
                <strong>Date</strong>
            </Text>
            {configuration?.approval?.projectManagerApproval?.useRates && (
                <Text>
                    <strong>Rate</strong>
                </Text>
            )}
            {configuration?.approval?.projectManagerApproval?.useRates && (
                <Text>
                    <strong>Activity</strong>
                </Text>
            )}
            <Text>
                <strong>Status</strong>
            </Text>
            <Text>
                <strong>Comment</strong>
            </Text>
        </div>
    );
};

const dragHandleStyles: IStackProps['styles'] = {
    root: {
        //
        display: 'flex',
        height: '100%',
        minWidth: 10,
        position: 'absolute',
        right: 0,
        touchAction: 'none',
        cursor: 'col-resize',
        transition: 'border 500ms',
        borderRight: '1px solid rgba(255,255,255,0)',
        '&:hover': {
            transition: 'border 300ms',
            borderRight: '1px solid rgba(255,255,255,0.5)',
        },
    },
};
